import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { AmplitudeService } from 'src/app/modules/amplitude/amplitude.service';

@Component({
  selector: 'bm-ext-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public tfaForm: FormGroup;
  public formSubmitted = false;
  public processing = false;
  public repeatRequestDisabled = true;
  public mode: 'login' | 'tfa' | 'none' = 'login';

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private amplitude: AmplitudeService
  ) { }

  ngOnInit() {
    document.querySelector('body').classList.add('page-login');
    this.form = this.fb.group({
        Login: ['', [Validators.required, Validators.maxLength(100)]],
        Password: ['', [Validators.required, Validators.maxLength(100)]],
        SavePassword: [true]
    });
    this.tfaForm = this.fb.group({
        Code: ['', [Validators.required, Validators.maxLength(10)]],
        SaveDevice: [false],
    });
    this.updateRepeatRequestDisabled();
    interval(1000).pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.updateRepeatRequestDisabled();
    });
  }

  ngOnDestroy(): void {
    document.querySelector('body').classList.remove('page-login');
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  login() {
    this.formSubmitted = true;
    if (!this.form.valid) {
      return;
    }
    if (this.processing) {
        return;
    }
    this.processing = true;
    this.auth.login({
        Login: this.form.value.Login.trim(),
        Password: this.form.value.Password,
    }, this.form.value.SavePassword).pipe(finalize(() => { this.processing = false; })).subscribe((r) => {
        this.updateRepeatRequestDisabled();
        if (r.RequiresTwoFactor) {
          this.showTfaForm();
          return;
        }
        if (!r.Success) {
          alert('Error!');
          return;
        }
        const path = this.auth.storedPath;
        this.mode = 'none';
        this.amplitude.setIdentityFromPayload(this.auth.payload);
        this.amplitude.logSigninEvent();
        this.router.navigateByUrl(path ? path : '/');
    }, (err) => {
        if (err.status === 500 || err.status === 503) {
            return;
        }
        this.form.get('Password').setValue('');
        alert('Invalid login or password!');
    });
  }

  showTfaForm(): void {
    this.formSubmitted = false;
    this.tfaForm.reset();
    this.mode = 'tfa';
  }

  repeatRequest(): void {
    this.updateRepeatRequestDisabled();
    if (this.repeatRequestDisabled || this.processing) {
      return;
    }
    this.processing = true;
    const tfaRequest = this.auth.tfaRequest;
    this.auth.login({
      Login: tfaRequest.Login,
      Password: tfaRequest.Password,
    }, this.form.value.SavePassword).pipe(finalize(() => { this.processing = false; })).subscribe((r) => {
      this.updateRepeatRequestDisabled();
      if (r.RequiresTwoFactor) {
        this.showTfaForm();
        return;
      }
      if (!r.Success) {
        alert('Error!');
        return;
      }
      const path = this.auth.storedPath;
      this.mode = 'none';
      this.amplitude.setIdentityFromPayload(this.auth.payload);
      this.amplitude.logSigninEvent();
      this.router.navigateByUrl(path ? path : '/');
    }, (err) => {
      if (err.status === 500 || err.status === 503) {
        return;
      }
      this.form.get('Password').setValue('');
      alert('Invalid login or password!');
    });
  }

  tfa(): void {
    if (this.processing) {
      return;
    }
    const tfaRequest = this.auth.tfaRequest;
    if (!tfaRequest) {
      return;
    }
    this.processing = true;
    this.auth.login({
      Login: tfaRequest.Login,
      Password: tfaRequest.Password,
      Code: this.tfaForm.value.Code,
    }, this.form.value.SavePassword).pipe(finalize(() => { this.processing = false; })).subscribe((r) => {
      this.updateRepeatRequestDisabled();
      if (r.RequiresTwoFactor) {
        this.showTfaForm();
        return;
      }
      if (!r.Success) {
        alert('Error!');
        return;
      }
      const path = this.auth.storedPath;
      this.mode = 'none';
      this.amplitude.setIdentityFromPayload(this.auth.payload);
      this.amplitude.logSigninEvent();
      this.router.navigateByUrl(path ? path : '/');
    }, (err) => {
      if (err.status === 500 || err.status === 503) {
        return;
      }
      this.form.get('Password').setValue('');
      alert('Invalid login or password!');
    });
  }

  private updateRepeatRequestDisabled(): void {
    const tfaRequest = this.auth.tfaRequest;
    this.repeatRequestDisabled = tfaRequest ?
      (
        tfaRequest.RequestTime ? ((new Date()).getTime() - tfaRequest.RequestTime.getTime() < 30000) : true
      ) :
      true;
  }

}

